import {makeAutoObservable} from "mobx";
import {CompatClient, Stomp} from "@stomp/stompjs";
import SockJS from "sockjs-client";
import {ApiName, getConfigForApi} from "../api";
import {cmsDetailStore, cmsStorageStore} from "./cms";
import {CustomerEventDto} from "../api/cms";
import {VehicleDataAcquisitionEventDto, VehicleDataAcquisitionStatusDto} from "../api/vda";
import {navigationStore} from "./NavigationStore";
import {vdaDetailStore, vdaOverviewStore, vdaPlacementStore} from "./vda";
import {userDetailStore, userListStore} from "./ums";
import {tenantStore} from "./TenantStore";
import {slsDetailStore, slsOverviewStore} from "./sls";
import {fmsOverviewStore, fmsPackageDetailStore, fmsPackageOverviewStore, fmsPicklistOverviewStore} from "./fms";
import {ScanDocumentApplicationEvent, ScanDocumentArchivedSocketEventDto} from "../api/sea";
import {seaDocumentArchiveStore, seaDocumentDetailStore, seaDocumentListStore} from "./sea";
import {debounce} from "../utils";
import {matchPath} from "react-router";
import {vhsDetailStore, vhsListStore} from "./vhs";
import {HistoryServiceEventPayloadDto} from "../api/vhs";
import {dashboardStore} from "./DashboardStore";
import {kpiCustomerStore} from "./kpi";
import {KpiServiceEventPayloadDto} from "../api/kpi";
import {scaDetailStore, scaOverviewStore} from "./sca";
import {stsDetailStore, stsOverviewStore} from "./sts";

export function extractTenantFromAuthority(iss: string | undefined) {
    const tenant = tenantStore.getTenantByAuthority(iss)
    if(tenant) {
        return tenant
    }
    throw new Error('Tenant not configured')
}

function getTenantId(token: String) {
    const parts = token.split(".")
    const jwtContent = JSON.parse(atob(parts[1])) as {iss?: string}

    return extractTenantFromAuthority(jwtContent.iss)
}

const createDebouncedLoad = (fn: () => Promise<any>, time = 1_000, maxInvocations = 30) =>
    debounce(fn, time, maxInvocations);

async function loadDashboard(customerId?: string) {
    await Promise.all([
        dashboardStore.load(customerId),
        kpiCustomerStore.init()
    ]).catch(() => {})
}

const listTimeDebounce = 30_000;
const debouncedSeaDocumentListRefresh = createDebouncedLoad(() => seaDocumentListStore.load(), listTimeDebounce);
const debouncedSeaDocumentListLoad = createDebouncedLoad(() => seaDocumentListStore.load());
const debouncedCmsStorageLoad = createDebouncedLoad(() => cmsStorageStore.load());
const debouncedVdaPlacementLoad = createDebouncedLoad(() => vdaPlacementStore.load());
const debouncedVdaOverviewLoad = createDebouncedLoad(() => vdaOverviewStore.load(), listTimeDebounce);
const debouncedVdaDetailLoad = createDebouncedLoad(() => vdaDetailStore.load());
const debouncedUserListLoad = createDebouncedLoad(() => userListStore.load(), listTimeDebounce);
const debouncedUserDetailLoad = createDebouncedLoad(() => userDetailStore.load());
const debouncedFmsPackageOverviewLoad = createDebouncedLoad(() => fmsPackageOverviewStore.load(), listTimeDebounce);
const debouncedFmsPackageDetailLoad = createDebouncedLoad(() => fmsPackageDetailStore.reload());
const debouncedFmsPicklistOverviewLoad = createDebouncedLoad(() => fmsPicklistOverviewStore.load(), listTimeDebounce);
const debouncedFmsOverviewLoad = createDebouncedLoad(() => fmsOverviewStore.load(), listTimeDebounce);
const debouncedSlsOverviewLoad = createDebouncedLoad(() => slsOverviewStore.load(), listTimeDebounce);
const debouncedSlsDetailLoad = createDebouncedLoad(() => slsDetailStore.load());
const debouncedVhsOverviewLoad = createDebouncedLoad(() => vhsListStore.load(), listTimeDebounce, 1000);
const debouncedVhsDetailLoad = createDebouncedLoad(() => vhsDetailStore.reload());
const debouncedDashboardOverviewLoad = createDebouncedLoad((customerId?: string) => loadDashboard(customerId), listTimeDebounce);
const debouncedScaOverviewList = createDebouncedLoad(() => scaOverviewStore.load(), listTimeDebounce, 1000)
const debouncedScaDetailLoad = createDebouncedLoad(() => scaDetailStore.load())
const debouncedStsOverviewList = createDebouncedLoad(() => stsOverviewStore.load(), listTimeDebounce, 1000)
const debouncedStsDetailLoad = createDebouncedLoad(() => stsDetailStore.load())


class SocketStore {
    private stompClients: Map<ApiName, CompatClient> = new Map<ApiName, CompatClient>()
    constructor() {
        makeAutoObservable(this)
    }

    public build(token: string) {
        const apis: ApiName[] = [
            // 'VRS', // aktuell kein websocket endpunkt vorhanden
            'SCA',
            'STS',
            'KPI',
            'VHS',
            'SEA',
            'VDA',
            'CMS',
            'UMS',
            'FMS',
            'SLS'
        ]

        apis.forEach(it => this.buildFor(token, it))
    }

    private buildFor(token: string | undefined, api: ApiName) {
        const {baseUrl} = getConfigForApi(api)
        const addition = token ? `?access_token=${token}` : ''
        const uri = `${baseUrl}/ws${addition}`;
        const stompClient = this.stompClients.get(api)
        if (stompClient) {
            stompClient.disconnect();
        }
        const client = Stomp.over(() => {
            return new SockJS(uri);
        });
        client.debug = (msg: string) => {
            //console.log(msg) // <-- activate if socket info is required
        }
        this.stompClients.set(api, client)
        if (!token) {
            return;
        }


        switch (api) {
            case "VRS":
                this.createVRSSubscriptions(client, token)
                break;
            case "SEA":
                this.createSEASubscriptions(client, token)
                break;
            case "CMS":
                this.createCMSSubscriptions(client, token)
                break;
            case "VDA":
                this.createVDASubscriptions(client, token)
                break;
            case "UMS":
                this.createUMSSubscriptions(client, token)
                break;
            case "FMS":
                this.createFMSSubscriptions(client, token)
                break;
            case "SLS":
                this.createSLSSubscriptions(client, token)
                break;
            case "VHS":
                this.createVHSSubscriptions(client, token)
                break;
            case "KPI":
                this.createKPISubscriptions(client, token)
                break;
            case "SCA":
                this.createSCASubscriptions(client, token)
                break
            case "STS":
                this.createSTSSubscriptions(client, token)
                break
        }
    }

    private createCMSSubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/customer/history`, async (result) => {
                const body = JSON.parse(result.body) as (CustomerEventDto | undefined)
                if (cmsDetailStore.id === body?.id) {
                    await cmsDetailStore.loadHistory()
                }
            })

            client.subscribe(`${prefix}/v1/warehouse/update`, async (result) => {
                debouncedCmsStorageLoad();
            })
        })
    }
    private createVRSSubscriptions(client: CompatClient, token: string) {

    }

    private createSEASubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/scan/document`, async (result) => {
                const body = JSON.parse(result.body) as (ScanDocumentApplicationEvent | undefined)
                const path = navigationStore.activePath
                if (path === '/sea/document') {
                    if (seaDocumentDetailStore.id && body?.payload === seaDocumentDetailStore.id) {
                        await seaDocumentDetailStore.load(body?.payload)
                    }
                    debouncedSeaDocumentListRefresh();
                }
            })
            client.subscribe(`${prefix}/v1/scan/document/archived`,async (result) => {
                const body = JSON.parse(result.body) as (ScanDocumentArchivedSocketEventDto | undefined)
                const path = navigationStore.activePath
                if (path === '/sea/document' && body) {
                    debouncedSeaDocumentListLoad();
                    seaDocumentArchiveStore.processSocketEvent(body)
                }
            })
        })
    }
    private createVDASubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/sls/placement`, async (result) => {
                const path = navigationStore.activePath
                if (path === '/vda/create' || path.startsWith('/vda/')) {
                    debouncedVdaPlacementLoad();
                }
            })
            client.subscribe(`${prefix}/v1/acquisition/vehicle`, async (result) => {
                const body = JSON.parse(result.body) as (VehicleDataAcquisitionEventDto | undefined)
                const status = body?.data?.status

                const path = navigationStore.activePath
                if (path === '/vda') {
                    debouncedVdaOverviewLoad();
                } else
                if (path !== '/vda/create' && path.startsWith('/vda/') && vdaDetailStore.id === body?.id) {
                    if (vdaDetailStore.dirty) {
                        console.log('vda is dirty and socket event happened')
                    } else if(status === VehicleDataAcquisitionStatusDto.ERROR) {
                        debouncedVdaDetailLoad();
                    } else if(status === VehicleDataAcquisitionStatusDto.COMPLETED) {
                        debouncedVdaDetailLoad();
                    }
                }
            })
        })
    }

    private createUMSSubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/user/change`, async (result) => {
                const id = JSON.parse(result.body)

                const path = navigationStore.activePath
                if (path === '/ums/user') {
                    debouncedUserListLoad();
                } else
                if (path !== '/ums/user/create' && path.startsWith('/ums/user/') && userDetailStore.id === id) {
                    if (userDetailStore.dirty) {
                        console.log('user is dirty and socket event happened')
                    } else {
                        debouncedUserDetailLoad();
                    }
                }
            })
        })
    }

    private createFMSSubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/fms/shipping_package`, async (result) => {
                const id = JSON.parse(result.body)?.id;

                const path = navigationStore.activePath
                if (path === '/fms/packages') {
                    debouncedFmsPackageOverviewLoad();
                } else
                if (matchPath('/fms/packages/:id', path) &&
                    !path.includes('create') &&
                    fmsPackageDetailStore.id === id) {
                    debouncedFmsPackageDetailLoad();
                }
            })
            client.subscribe(`${prefix}/v1/fms/picklist`, async (result) => {
                const path = navigationStore.activePath
                if (path === '/fms/picklist') {
                    debouncedFmsPicklistOverviewLoad();
                }
            })
            client.subscribe(`${prefix}/v1/fms/shipment_item`, async (result) => {
                const path = navigationStore.activePath
                if (path === '/fms') {
                    debouncedFmsOverviewLoad();
                }
            })
        })
    }

    private createSLSSubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/storage/placement`, async (result) => {
                const path = navigationStore.activePath
                const payload = JSON.parse(result.body) as string

                if (path === '/sls') {
                    debouncedSlsOverviewLoad()
                } else if (path.startsWith('/sls/')) {
                    if (payload && payload === slsDetailStore.id) {
                        debouncedSlsDetailLoad()
                    }
                }
            })
        })
    }

    private createVHSSubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/history`, async (result) => {
                const path = navigationStore.activePath
                if (path.startsWith('/vhs')) {
                    if (path === '/vhs') {
                        debouncedVhsOverviewLoad()
                    } else {
                        const payload = JSON.parse(result.body) as HistoryServiceEventPayloadDto;
                        const payloadPath = `/vhs/${payload.customerId}/${payload.vin}`
                        if (path === payloadPath) {
                            debouncedVhsDetailLoad()
                        }
                    }
                }
            })
        })
    }

    private createKPISubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/kpi`, async (result) => {
                const path = navigationStore.activePath
                const payload = JSON.parse(result.body) as KpiServiceEventPayloadDto;
                if (path === '/dashboard') {
                    debouncedDashboardOverviewLoad(payload?.customerId)
                }
            })
        })
    }

    private createSCASubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/sca`, async (result) => {
                const path = navigationStore.activePath
                const payload = JSON.parse(result.body) as string
                if (path === '/sca') {
                    debouncedScaOverviewList()
                } else if (path.startsWith('/sca/')) {
                    if (payload && payload === scaDetailStore.id) {
                        debouncedScaDetailLoad()
                    }
                }
            })
        })
    }

    private createSTSSubscriptions(client: CompatClient, token: string) {
        client.connect({}, () => {
            const prefix = `/tenant/${getTenantId(token)}`
            client.subscribe(`${prefix}/v1/sts`, async (result) => {
                const path = navigationStore.activePath
                const payload = JSON.parse(result.body) as string
                if (path === '/sts') {
                    debouncedStsOverviewList()
                } else if (path.startsWith('/sts/')) {
                    if (payload && payload === stsDetailStore.id) {
                        debouncedStsDetailLoad()
                    }
                }
            })
        })
    }
}

const socketStore = new SocketStore()

export {
    SocketStore,
    socketStore
}
